/**
 * TinyMCE Insert Form Plugin
 *
 * Provides a toolbar button that opens a dialog to select a published form
 * and inserts the corresponding shortcode into the editor content.
 */

export class InsertFormPlugin {
    constructor(editor) {
        this.editor = editor;
        this.forms = [];
    }

    init() {
        this.registerToolbarButton();
        this.registerMenuItem();
    }

    registerToolbarButton() {
        this.editor.ui.registry.addIcon('formicon', '<svg width="24" height="24"><path d="M5 2h14a2 2 0 0 1 2 2v16a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2Zm0 2v16h14V4H5Zm2 4h10v1.5H7V8Zm0 4h10v1.5H7v-1.5Zm0 4h6v1.5H7v-1.5Z" fill-rule="evenodd"/></svg>');

        this.editor.ui.registry.addButton('insertform', {
            icon: 'formicon',
            tooltip: 'Insert Form',
            onAction: () => {
                this.openDialog();
            }
        });
    }

    registerMenuItem() {
        this.editor.ui.registry.addMenuItem('insertform', {
            icon: 'formicon',
            text: 'Insert Form',
            onAction: () => {
                this.openDialog();
            }
        });
    }

    async openDialog() {
        try {
            const response = await fetch('/admin/forms.json');
            if (!response.ok) throw new Error('Failed to load forms');
            this.forms = await response.json();
        } catch (err) {
            this.editor.notificationManager.open({
                text: 'Could not load forms. Please try again.',
                type: 'error',
                timeout: 3000
            });
            return;
        }

        const publishedForms = this.forms.filter(f => f.status === 'published');
        const draftForms = this.forms.filter(f => f.status === 'draft');

        if (this.forms.length === 0) {
            this.editor.notificationManager.open({
                text: 'No forms found. Create a form first.',
                type: 'warning',
                timeout: 3000
            });
            return;
        }

        const formItems = [
            ...publishedForms.map(f => ({
                text: `${f.title || f.name}`,
                value: f.name
            })),
            ...draftForms.map(f => ({
                text: `${f.title || f.name} (draft)`,
                value: f.name
            }))
        ];

        this.editor.windowManager.open({
            title: 'Insert Form',
            size: 'normal',
            body: {
                type: 'panel',
                items: [
                    {
                        type: 'selectbox',
                        name: 'formName',
                        label: 'Select a form',
                        items: formItems
                    },
                    {
                        type: 'checkbox',
                        name: 'hideTitle',
                        label: 'Hide form title'
                    },
                    {
                        type: 'checkbox',
                        name: 'hideDescription',
                        label: 'Hide form description'
                    }
                ]
            },
            buttons: [
                { type: 'cancel', text: 'Cancel' },
                { type: 'submit', text: 'Insert', primary: true }
            ],
            initialData: {
                formName: formItems[0]?.value || '',
                hideTitle: false,
                hideDescription: false
            },
            onSubmit: (api) => {
                const data = api.getData();
                this.insertFormShortcode(data);
                api.close();
            }
        });
    }

    insertFormShortcode(data) {
        const params = [`name: "${data.formName}"`];

        if (data.hideTitle) {
            params.push('show_title: false');
        }
        if (data.hideDescription) {
            params.push('show_description: false');
        }

        const shortcode = `{partial: "wps_pages/shared/wps_form", params: {${params.join(', ')}}}`;

        this.editor.focus();
        this.editor.insertContent(`<p>${shortcode}</p>`);
        this.editor.save();
    }
}
